import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Grid,
  Typography,
  TextField,
  Button as MuiButton,
  Checkbox,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const CalculationTab = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  
  // Form state
  const [formQuotesValues, setFormQuotesValues] = useState({
    laborCost: 0,
    estdHours: 0,
    calloutFee: false,
    calloutFeeAmount: 150, // Default callout fee amount
    serviceCharge: 0,
    taxRate: 15, // Default tax rate
    items: []
  });
  
  // Other state
  const [plumberId, setPlumberId] = useState('1001');
  const [userId, setUserId] = useState('1001');
  const [quoteCounter, setQuoteCounter] = useState(1);
  const [quoteId, setQuoteId] = useState(null);
  const [quoteSummary, setQuoteSummary] = useState(null);
  const [openSuccessDialog, setOpenSuccessDialog] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  
  // Calculate totals
  const totalPartsEstimate = formQuotesValues.items.reduce(
    (sum, item) => sum + parseFloat(item.price || 0), 
    0
  );
  
  const subtotal = 
    parseFloat(formQuotesValues.laborCost || 0) + 
    parseFloat(formQuotesValues.serviceCharge || 0) + 
    totalPartsEstimate + 
    (formQuotesValues.calloutFee ? formQuotesValues.calloutFeeAmount : 0);
  
  const taxAmount = subtotal * (formQuotesValues.taxRate / 100);
  const totalPrice = (subtotal + taxAmount).toFixed(2);

  // Get auth token
  const getAuthToken = () => {
    return localStorage.getItem('authToken');
  };

  // Form handlers
  const handleLaborChange = (field, value) => {
    setFormQuotesValues({
      ...formQuotesValues,
      [field]: value
    });
  };

  const handleItemChange = (index, field, value) => {
    const updatedItems = [...formQuotesValues.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };
    setFormQuotesValues({
      ...formQuotesValues,
      items: updatedItems
    });
  };

  const addItemsNeeded = () => {
    setFormQuotesValues({
      ...formQuotesValues,
      items: [...formQuotesValues.items, { name: '', price: '' }]
    });
  };

  // Create quote
  const createQuote = async () => {
    if (!jobId || 
        parseFloat(formQuotesValues.laborCost) <= 0 || 
        parseFloat(formQuotesValues.estdHours) <= 0) {
      setErrorMessage('Please fill in all required fields. Labor cost and estimated hours must be greater than 0.');
      setShowError(true);
      return;
    }

    try {
      setIsLoading(true);

      const token = getAuthToken();
      if (!token) {
        setErrorMessage('Not authenticated. Please log in.');
        setShowError(true);
        setIsLoading(false);
        return;
      }

      // Increment IDs for new quote
      const newPlumberId = parseInt(plumberId) + quoteCounter;
      const newUserId = parseInt(userId) + quoteCounter;

      const response = await axios.post(
        'https://quotemanagement-497524552274.us-central1.run.app/quotes/create',
        {
          jobId: parseInt(jobId),
          plumberId: newPlumberId,
          userId: newUserId,
          laborCost: parseFloat(formQuotesValues.laborCost),
          estdHours: parseFloat(formQuotesValues.estdHours)
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setQuoteId(response.data.id);
      setQuoteCounter(quoteCounter + 1);
      
      // Submit items if any exist
      if (formQuotesValues.items.length > 0) {
        await submitItems(response.data.id);
      }
      
      // Get quote summary
      await getQuoteSummary(response.data.id);
      
      // Show success dialog
      setOpenSuccessDialog(true);
      setIsLoading(false);
    } catch (err) {
      console.error("Create quote error:", err);
      setErrorMessage(err.response?.data?.message || 'Failed to create quote. Please try again.');
      setShowError(true);
      setIsLoading(false);
    }
  };

  // Submit items
  const submitItems = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const formattedItems = formQuotesValues.items.map(item => ({
        description: item.name || "Unnamed item",  // Ensure there's a default if name is empty
        cost: parseFloat(item.price || 0)         // Ensure there's a default if price is empty
      }));

      await axios.post(
        `https://quotemanagement-497524552274.us-central1.run.app/quotes/${id || quoteId}/items`,
        {
          itemsNeeded: formattedItems
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
    } catch (err) {
      console.error("Submit items error:", err);
      throw err;
    }
  };

  // Get quote summary
  const getQuoteSummary = async (id) => {
    try {
      const token = getAuthToken();
      if (!token) return;

      const response = await axios.get(
        `https://quotemanagement-497524552274.us-central1.run.app/quotes/${id || quoteId}/summary`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setQuoteSummary(response.data);
    } catch (err) {
      console.error("Get quote summary error:", err);
      throw err;
    }
  };

  // Dialog and error handlers
  const handleCloseDialog = () => {
    setOpenSuccessDialog(false);
  };

  const handleCloseError = () => {
    setShowError(false);
  };

  // Helper function to safely display item details
  const formatItemText = (item) => {
    if (typeof item === 'string') return item;
    if (!item) return "Unknown item";
    
    const description = item.description || "Unnamed item";
    const cost = typeof item.cost === 'number' ? item.cost.toFixed(2) : '0.00';
    
    return `${description}: R ${cost}`;
  };

  return (
    <Grid>
      <Grid padding="5%">
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackIcon />
        </IconButton>
        <Typography fontSize="20px" fontWeight="bold">Create Quote</Typography>
      </Grid>

      <Grid padding="5%">
        <Typography fontSize="16px" fontWeight="bold">
          Create a Quote for Job ID: {jobId}
        </Typography>

        <TextField
          label="Labor Cost"
          type="number"
          value={formQuotesValues.laborCost}
          onChange={(e) => handleLaborChange("laborCost", e.target.value)}
          fullWidth
          margin="normal"
        />

        <TextField
          label="Estimated Hours"
          type="number"
          value={formQuotesValues.estdHours}
          onChange={(e) => handleLaborChange("estdHours", e.target.value)}
          fullWidth
          margin="normal"
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={formQuotesValues.calloutFee}
              onChange={(e) => handleLaborChange("calloutFee", e.target.checked)}
            />
          }
          label="Include Callout Fee"
        />
      </Grid>

      <Grid padding="5%">
        <Typography fontSize="16px" fontWeight="bold">Add Items</Typography>

        {formQuotesValues.items.map((item, index) => (
          <Grid container spacing={2} key={index} style={{ marginBottom: "10px" }}>
            <Grid item xs={5}>
              <TextField
                label="Item Name"
                value={item.name}
                onChange={(e) => handleItemChange(index, "name", e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Item Price"
                type="number"
                value={item.price}
                onChange={(e) => handleItemChange(index, "price", e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        ))}

        <MuiButton variant="outlined" onClick={addItemsNeeded} style={{ marginTop: '10px' }}>
          Add More Items
        </MuiButton>
      </Grid>

      <Grid padding="5%">
        <Typography fontSize="16px" fontWeight="bold">Quote Summary</Typography>
        {quoteSummary ? (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Job ID</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>{quoteSummary.jobId}</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Quote ID</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>{quoteSummary.quoteId}</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Labor Cost</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>R {quoteSummary.laborCost?.toFixed(2)}</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Items Needed:</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}></td>
              </tr>
              {quoteSummary.itemsNeeded && quoteSummary.itemsNeeded.length > 0 ? (
                quoteSummary.itemsNeeded.map((item, index) => (
                  <tr key={index}>
                    <td colSpan="2" style={{ padding: "8px", borderBottom: "1px solid #ddd", paddingLeft: "20px" }}>
                      {formatItemText(item)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" style={{ padding: "8px", borderBottom: "1px solid #ddd", paddingLeft: "20px" }}>
                    No items added
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Total Cost</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd", fontWeight: "bold" }}>
                  R {quoteSummary.totalCost?.toFixed(2)}
                </td>
              </tr>
            </tbody>
          </table>
        ) : (
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Labor Cost</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>R {formQuotesValues.laborCost}</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Estimated Hours</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>{formQuotesValues.estdHours} hrs</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Callout Fee</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>
                  R {formQuotesValues.calloutFee ? formQuotesValues.calloutFeeAmount : 0}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Service Charge</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>R {formQuotesValues.serviceCharge}</td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Items:</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}></td>
              </tr>
              {formQuotesValues.items.length > 0 ? (
                formQuotesValues.items.map((item, index) => (
                  <tr key={index}>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ddd", paddingLeft: "20px" }}>
                      {item.name || "Unnamed item"}
                    </td>
                    <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>
                      R {parseFloat(item.price || 0).toFixed(2)}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="2" style={{ padding: "8px", borderBottom: "1px solid #ddd", paddingLeft: "20px" }}>
                    No items added
                  </td>
                </tr>
              )}
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>
                  Tax ({formQuotesValues.taxRate}%)
                </td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>
                  R {taxAmount.toFixed(2)}
                </td>
              </tr>
              <tr>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd" }}>Total</td>
                <td style={{ padding: "8px", borderBottom: "1px solid #ddd", fontWeight: "bold" }}>R {totalPrice}</td>
              </tr>
            </tbody>
          </table>
        )}
      </Grid>

      <Grid padding="5%">
        <MuiButton 
          variant="contained" 
          onClick={createQuote} 
          disabled={quoteId !== null || isLoading}
          fullWidth
        >
          {isLoading ? "Creating..." : quoteId ? "Quote Created" : "Create Quote"}
        </MuiButton>
      </Grid>

      {/* Success Dialog */}
      <Dialog
        open={openSuccessDialog}
        onClose={handleCloseDialog}
        aria-labelledby="success-dialog-title"
        aria-describedby="success-dialog-description"
      >
        <DialogTitle id="success-dialog-title">Quote Created Successfully</DialogTitle>
        <DialogContent>
          <Typography id="success-dialog-description">
            Your quote has been successfully created for Job ID: {jobId}.
            {quoteSummary && (
              <>
                <br /><br />
                <strong>Quote Details:</strong><br />
                Quote ID: {quoteSummary.quoteId}<br />
                Labor Cost: R {quoteSummary.laborCost?.toFixed(2)}<br />
                {quoteSummary.itemsNeeded && quoteSummary.itemsNeeded.length > 0 && (
                  <>
                    <strong>Items:</strong><br />
                    {quoteSummary.itemsNeeded.map((item, index) => (
                      <span key={index}>
                        {formatItemText(item)}<br />
                      </span>
                    ))}
                  </>
                )}
                Total Cost: R {quoteSummary.totalCost?.toFixed(2)}
              </>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <MuiButton onClick={handleCloseDialog} color="primary">
            OK
          </MuiButton>
        </DialogActions>
      </Dialog>
      
      {/* Error Snackbar */}
      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {errorMessage}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default CalculationTab;